import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Code2, ExternalLink, Sparkles, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useCanonical } from "../lib/seoHelpers";
import { safeJsonLd } from "../lib/safeJsonLd";

/**
 * /tools/embed — the linkable-asset landing page.
 *
 * Publishers, journalists, and bloggers land here from search or organic
 * mentions ("free suspension analyzer widget"). They pick a size, grab the
 * iframe snippet, and paste it on their site. Every embed = one dofollow
 * backlink to socialdisputeco.com.
 *
 * We keep the snippet vanilla HTML — no npm install, no JS SDK, no build
 * step. Zero friction is the whole point.
 */
export default function EmbedBuilderPage() {
  useCanonical("https://socialdisputeco.com/tools/embed");
  useEffect(() => {
    document.title = "Free Suspension Analyzer Widget — Embed on Your Site | Social Dispute Co";
    const desc = "Add our free suspension analyzer to any site with one line of HTML. Zero cost, no signup, no strings. A useful tool for your readers.";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
  }, []);

  const [height, setHeight] = useState(720);
  const [copied, setCopied] = useState(false);

  const embedUrl = `https://socialdisputeco.com/embed/analyzer`;
  const snippet = useMemo(
    () => `<iframe src="${embedUrl}" width="100%" height="${height}" style="border:0;border-radius:12px;background:#1a1c1b;" title="Free Suspension Analyzer by Social Dispute Co" loading="lazy"></iframe>`,
    [embedUrl, height]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Silent
    }
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Free Suspension Analyzer Widget",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web Browser",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    description: "Embeddable suspension analyzer for social media appeals. Free for any website.",
    url: "https://socialdisputeco.com/tools/embed",
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="embed-builder-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(softwareSchema) }}
      />

      <Header />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 bg-gradient-to-br from-[#3f4816]/40 to-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <p className="text-xs uppercase tracking-widest text-[#d9fb06] font-mono mb-3 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              Free tool · One line of HTML · Zero signup
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#dfddd6] uppercase leading-[0.95] mb-5">
              Embed our free suspension analyzer on your site
            </h1>
            <p className="text-lg md:text-xl text-[#888680] max-w-2xl leading-relaxed">
              A useful tool for your readers. A backlink for us. That&apos;s the deal — no hidden costs,
              no data collection on your users, no tracking pixels.
            </p>
          </div>
        </section>

        {/* Live preview + snippet */}
        <section className="py-12 md:py-16">
          <div className="max-w-6xl mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-8">
            {/* Live preview */}
            <div>
              <h2 className="text-xl font-black text-[#d9fb06] uppercase mb-4">Live preview</h2>
              <div className="rounded-xl overflow-hidden border border-[#3f4816]/50 bg-[#252825]">
                <iframe
                  src="/embed/analyzer"
                  width="100%"
                  height={height}
                  style={{ border: 0, display: "block" }}
                  title="Analyzer widget preview"
                  data-testid="embed-preview-iframe"
                />
              </div>
              <p className="text-xs text-[#888680] mt-2">
                This is exactly what your visitors will see.
              </p>
            </div>

            {/* Snippet builder */}
            <div>
              <h2 className="text-xl font-black text-[#d9fb06] uppercase mb-4">Get the code</h2>

              <label className="block text-xs uppercase tracking-wider text-[#888680] mb-2">
                Height (px)
              </label>
              <input
                type="range"
                min={520}
                max={900}
                step={20}
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                className="w-full mb-2 accent-[#d9fb06]"
                data-testid="embed-height-slider"
              />
              <p className="text-sm text-[#dfddd6] mb-6 font-mono">{height}px</p>

              <div className="relative">
                <pre
                  className="bg-[#0f100f] border border-[#3f4816]/50 rounded-xl p-4 text-xs text-[#d9fb06] font-mono overflow-x-auto"
                  data-testid="embed-snippet-pre"
                >
                  {snippet}
                </pre>
                <button
                  onClick={handleCopy}
                  className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-xs font-bold hover:bg-[#d9fb06]/90 transition"
                  data-testid="embed-copy-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              <div className="mt-6 rounded-xl border border-[#3f4816]/40 bg-[#302f2c] p-4 space-y-2">
                <p className="text-xs uppercase tracking-widest text-[#888680] font-mono flex items-center gap-2">
                  <Code2 className="w-3.5 h-3.5" />
                  Installation
                </p>
                <ol className="text-sm text-[#dfddd6] space-y-1.5 list-decimal list-inside">
                  <li>Copy the snippet above</li>
                  <li>Paste it anywhere in your page&apos;s HTML</li>
                  <li>Save. Done — the analyzer will render immediately.</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* What you get / What we get */}
        <section className="py-16 bg-[#302f2c]">
          <div className="max-w-5xl mx-auto px-5 md:px-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase text-center mb-10">
              The deal, in plain English
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-xl border border-[#3f4816]/40 bg-[#1a1c1b] p-6">
                <h3 className="text-lg font-bold text-[#d9fb06] mb-3">You get</h3>
                <ul className="space-y-2 text-[#dfddd6] text-sm">
                  <li>• A genuinely useful free tool your readers will actually engage with</li>
                  <li>• Zero cost, zero signup, zero maintenance</li>
                  <li>• No tracking pixels attached to your visitors</li>
                  <li>• A clean, mobile-responsive iframe that works everywhere</li>
                  <li>• Time-on-page + engagement lift (widget is interactive)</li>
                </ul>
              </div>
              <div className="rounded-xl border border-[#3f4816]/40 bg-[#1a1c1b] p-6">
                <h3 className="text-lg font-bold text-[#d9fb06] mb-3">We get</h3>
                <ul className="space-y-2 text-[#dfddd6] text-sm">
                  <li>• A dofollow backlink in the widget attribution footer</li>
                  <li>• Real usage data on which platforms/reasons users search for</li>
                  <li>• A path for engaged users to become customers if they need paid help</li>
                </ul>
                <p className="text-xs text-[#888680] mt-4">
                  That&apos;s it. No hidden retargeting. No email harvesting. No fine print.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-2xl mx-auto px-5 md:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-black text-[#d9fb06] uppercase mb-4">
              Try the full-page version first
            </h2>
            <p className="text-[#888680] mb-6">
              See exactly what your readers will see, in the full site chrome.
            </p>
            <Link to="/analyze">
              <Button size="lg" className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold px-8">
                Open Full Analyzer
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <p className="mt-6 text-xs text-[#888680]">
              Questions? Email{" "}
              <a href="mailto:hello@socialdisputeco.com" className="text-[#d9fb06] hover:underline">
                hello@socialdisputeco.com
              </a>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
